import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { socket } from '../utils/socket';
import GameBoard from './GameBoard';
import { ValidCell } from '../types';
import ScoreHistoryModal from './ScoreHistoryModal';

import SimpleScoreBlock from './SimpleScoreBlock';

interface GameState {
  id: string;
  gameMasters: string[];
  teamA: string[];
  teamB: string[];
  round: number;
  chosenLocationsA: ValidCell[];
  chosenLocationsB: ValidCell[];
  currentSelections: {
    teamA?: { position: ValidCell | null; confirmed: boolean };
    teamB?: { position: ValidCell | null; confirmed: boolean };
  };
  scores: {
    teamA: number;
    teamB: number;
  };
  alignments: {
    teamA: number;
    teamB: number;
  };
  status: 'waiting' | 'playing' | 'finished';
  board: {
    [key in ValidCell]?: 'A' | 'B';
  };
  blockedPositions: ValidCell[];
  masterBlockedPositions: ValidCell[];
  competitorSharesMarket: boolean;
  financeMode: boolean;
  shopPriceMultipliers: { [key in ValidCell]?: number };
  roundScores?: Array<{
    teamA: number;
    teamB: number;
  }>;
}

interface TeamInterfaceProps {
  team: 'A' | 'B';
}

const TeamInterface: React.FC<TeamInterfaceProps> = ({ team }) => {
  const { gameId } = useParams<{ gameId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [choice, setChoice] = useState<string>('');
  const [competitorMessage, setCompetitorMessage] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const hasJoined = useRef(false);

  useEffect(() => {
    if (!gameId) {
      navigate('/');
      return;
    }

    const handleConnect = () => {
      setIsConnected(true);
      setError(null);
      if (!hasJoined.current) {
        socket.emit('joinGame', { gameId, role: `team${team}` });
        hasJoined.current = true;
      }
    };

    const handleDisconnect = () => {
      setIsConnected(false);
      setError('Disconnected from server');
      hasJoined.current = false;
    };

    const handleGameState = (state: GameState) => {
      setGameState(state);
      setError(null);
    };

    const handleError = (errorMessage: string) => {
      setError(errorMessage);
    };

    const handlePositionConflict = (data: { position: ValidCell; message: string }) => {
      setError(t('positionConflict'));
      setChoice('');
    };

    const handleCompetitorEntered = (data: { position: ValidCell; message: string }) => {
      setCompetitorMessage(t('competitorEntered', { position: data.position }));
      setTimeout(() => setCompetitorMessage(null), 8000);
    };

    const handlePositionBlockedByCompetitor = (data: { message: string }) => {
      setError(data.message);
      setChoice('');
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('gameState', handleGameState);
    socket.on('error', handleError);
    socket.on('positionConflict', handlePositionConflict);
    socket.on('competitorEntered', handleCompetitorEntered);
    socket.on('positionBlockedByCompetitor', handlePositionBlockedByCompetitor);

    if (isConnected && !hasJoined.current) {
      socket.emit('joinGame', { gameId, role: `team${team}` });
      hasJoined.current = true;
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('gameState', handleGameState);
      socket.off('error', handleError);
      socket.off('positionConflict', handlePositionConflict);
      socket.off('competitorEntered', handleCompetitorEntered);
      socket.off('positionBlockedByCompetitor', handlePositionBlockedByCompetitor);
    };
  }, [gameId, team, navigate, isConnected, t]);

  const handleSubmit = () => {
    if (!gameState || !gameId || !choice) return;

    const validChoice = choice.toUpperCase();
    if (!/^[MKDLW][1-5]$/.test(validChoice)) {
      setError(t('errors.invalidPosition'));
      return;
    }

    if (gameState.blockedPositions.includes(validChoice as ValidCell)) {
      setError(t('errors.positionBlocked'));
      return;
    }

    if (gameState.masterBlockedPositions.includes(validChoice as ValidCell)) {
      setError(t('errors.positionBlockedByCompetitor'));
      return;
    }

    socket.emit('makeMove', { gameId, team, position: validChoice as ValidCell });
    setChoice('');
  };

  const handleConfirmChoice = () => {
    if (!gameState || !gameId) return;
    socket.emit('confirmMove', { gameId, team });
  };

  const handleResetChoice = () => {
    if (!gameState || !gameId) return;
    socket.emit('resetMove', { gameId, team });
    setChoice('');
  };

  const handleCellClick = (cell: ValidCell) => {
    if (!gameState || !gameId || !isMyTurn || mySelection?.confirmed) return;

    // Check if position is valid and not blocked
    if (gameState.blockedPositions.includes(cell)) {
      setError(t('errors.positionBlocked'));
      return;
    }

    // Check if position is blocked by master (competitor)
    if (gameState.masterBlockedPositions.includes(cell)) {
      setError(t('errors.positionBlockedByCompetitor'));
      return;
    }

    // If it's a direct click, make the move immediately
    socket.emit('makeMove', { gameId, team, position: cell });
    setChoice(''); // Clear the text input since we're using click selection
  };

  if (!isConnected || !gameState) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
        <h1 className="text-4xl font-bold mb-4">{t('team')} {team}</h1>
        <p className="text-xl">{!isConnected ? t('connecting') : t('loading')}</p>
      </div>
    );
  }

  const mySelection = team === 'A' ? gameState.currentSelections.teamA : gameState.currentSelections.teamB;
  const isMyTurn = gameState.status === 'playing' && (!mySelection?.position || !mySelection.confirmed);

  // Generate round history from current round and scores
  const roundHistory = Array.from({ length: gameState.round }, (_, i) => ({
    round: i + 1,
    teamA: Math.round(gameState.roundScores?.[i]?.teamA || 0),
    teamB: Math.round(gameState.roundScores?.[i]?.teamB || 0)
  }));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white p-4 transition-colors">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-500 dark:bg-red-600 text-white rounded-lg animate-fade-in shadow-md">
            {error}
          </div>
        )}

        {competitorMessage && (
          <div className="mb-4 p-4 bg-purple-500 dark:bg-purple-600 text-white rounded-lg animate-fade-in shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-2xl">⚠️</span>
              <span className="font-bold">{competitorMessage}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <GameBoard
              chosenLocationsA={gameState.chosenLocationsA}
              chosenLocationsB={gameState.chosenLocationsB}
              currentSelections={{
                teamA: team === 'A' ? mySelection?.position : undefined,
                teamB: team === 'B' ? mySelection?.position : undefined
              }}
              blockedPositions={gameState.blockedPositions}
              masterBlockedPositions={gameState.masterBlockedPositions}
              competitorSharesMarket={gameState.competitorSharesMarket}
              board={gameState.board}
              onCellClick={handleCellClick}
              clickableForTeam={isMyTurn && !mySelection?.confirmed}
              financeMode={gameState.financeMode}
              shopPriceMultipliers={gameState.shopPriceMultipliers}
            />
          </div>

          <div className="space-y-4">
            {/* Score Block */}
            <div style={{ position: 'relative' }}>
              <SimpleScoreBlock
                teamAScore={gameState.scores.teamA}
                teamBScore={gameState.scores.teamB}
                teamAAlignments={gameState.alignments.teamA}
                teamBAlignments={gameState.alignments.teamB}
                currentRound={gameState.round}
                maxRounds={12}
                financeMode={gameState.financeMode}
              />
              {/* History Button at Bottom */}
              <button
                onClick={() => setShowHistoryModal(true)}
                style={{
                  position: 'absolute',
                  bottom: '12px',
                  right: '12px',
                  background: 'rgba(59, 130, 246, 0.2)',
                  border: '1px solid rgba(59, 130, 246, 0.4)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  zIndex: 10,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Voir l'historique des scores"
              >
                📊
              </button>
            </div>

            {/* Compact Action Panel - No title, no instruction */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={choice}
                onChange={(e) => setChoice(e.target.value)}
                placeholder={t('enterPosition')}
                className="w-full p-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border-2 border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none text-sm mb-3"
                disabled={!isMyTurn || mySelection?.confirmed}
              />

              <div className="flex gap-2">
                <button
                  onClick={handleSubmit}
                  className={`
                    flex-1 py-2 px-4 rounded-lg font-bold shadow-md text-sm
                    transition-all duration-200
                    ${isMyTurn && !mySelection?.confirmed
                      ? 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white'
                      : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed opacity-50 text-gray-500 dark:text-gray-400'
                    }
                  `}
                  disabled={!isMyTurn || !choice || mySelection?.confirmed}
                >
                  {t('submit')}
                </button>

                {mySelection?.position && !mySelection.confirmed && (
                  <>
                    <button
                      onClick={handleConfirmChoice}
                      className="flex-1 py-2 px-4 rounded-lg font-bold bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white transition-all duration-200 shadow-md text-sm"
                    >
                      {t('confirm')}
                    </button>
                    <button
                      onClick={handleResetChoice}
                      className="flex-1 py-2 px-4 rounded-lg font-bold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white transition-all duration-200 shadow-md text-sm"
                    >
                      {t('reset')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Status Message - Compact */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-lg border border-gray-200 dark:border-gray-700">
              <p className="text-center text-sm">
                {isMyTurn
                  ? mySelection?.position
                    ? mySelection.confirmed
                      ? t('waitingForOthers')
                      : t('confirmYourChoice')
                    : t('yourTurn')
                  : t('waitingForTurn')}
              </p>
            </div>
          </div>

          {/* Score History Modal */}
          <ScoreHistoryModal
            isOpen={showHistoryModal}
            onClose={() => setShowHistoryModal(false)}
            rounds={gameState.roundScores || []}
            currentRound={gameState.round}
          />
        </div>
      </div>
    </div>
  );
};

export default TeamInterface;
